import { useMemo, useState } from 'react'
import { useStore } from '../store/store'
import type { KellyMode } from '../domain/types'
import { correlatedExposureWarnings, monteCarloBankroll, riskOfRuin } from '../lib/math'
import { exposureBreakdown, exposureItems, bankrollNow } from '../store/selectors'
import { fmtCurrency, fmtPct, fmtPctValue } from '../lib/format'
import { Banner, Button, Card, NumberField, PageHead, Pill, SelectField, Stat } from '../components/ui'
import { LineChart } from '../components/charts'

export function RiskManager() {
  const store = useStore()
  const risk = store.risk
  const setRisk = store.setRisk
  const bankroll = useMemo(() => bankrollNow(store), [store])

  const breakdown = useMemo(() => exposureBreakdown(store), [store])
  const warnings = useMemo(
    () => correlatedExposureWarnings(exposureItems(store), bankroll, { maxGroupPct: risk.maxCorrelatedPct, maxSinglePct: risk.maxSinglePositionPct }),
    [store, bankroll, risk.maxCorrelatedPct, risk.maxSinglePositionPct],
  )

  const [winProb, setWinProb] = useState(0.53)
  const [payout, setPayout] = useState(2)
  const [stakeFraction, setStakeFraction] = useState(0.05)
  const [bets, setBets] = useState(100)

  const perBetReturns = useMemo(() => {
    const arr: number[] = []
    const wins = Math.round(winProb * 100)
    for (let i = 0; i < 100; i++) arr.push(i < wins ? payout - 1 : -1)
    return arr
  }, [winProb, payout])

  const ror = useMemo(
    () => riskOfRuin({ bankroll, winProb, stakeFraction, payoutMultiple: payout, bets, ruinThresholdPct: 0.5, simulations: 2000, seed: 4 }),
    [bankroll, winProb, stakeFraction, payout, bets],
  )
  const mc = useMemo(
    () => monteCarloBankroll({ bankroll, perBetReturns, bets, stakeFraction, simulations: 400, seed: 9 }),
    [bankroll, perBetReturns, bets, stakeFraction],
  )

  return (
    <div className="stack">
      <PageHead title="Bankroll & Risk Manager">
        Your limits are the only thing standing between a bad streak and a blown account. Set them once, then let EdgeLab nag you when you drift.
      </PageHead>

      <div className="grid cols-2">
        <Card title="Bankroll & limits">
          <div className="grid cols-2">
            <NumberField label="Bankroll" value={risk.bankroll} onChange={(v) => setRisk({ bankroll: v })} step={100} />
            <NumberField label="Max risk per trade" value={risk.maxRiskPerTradePct} onChange={(v) => setRisk({ maxRiskPerTradePct: v })} step={0.005} min={0} max={0.2} hint={fmtPct(risk.maxRiskPerTradePct, 1)} />
            <NumberField label="Max daily loss" value={risk.maxDailyLossPct} onChange={(v) => setRisk({ maxDailyLossPct: v })} step={0.005} min={0} max={0.5} hint={fmtPct(risk.maxDailyLossPct, 1)} />
            <NumberField label="Max weekly loss" value={risk.maxWeeklyLossPct} onChange={(v) => setRisk({ maxWeeklyLossPct: v })} step={0.005} min={0} max={0.5} hint={fmtPct(risk.maxWeeklyLossPct, 1)} />
            <NumberField label="Max single position" value={risk.maxSinglePositionPct} onChange={(v) => setRisk({ maxSinglePositionPct: v })} step={0.01} min={0} max={1} hint={fmtPct(risk.maxSinglePositionPct, 0)} />
            <NumberField label="Max per group" value={risk.maxExposurePerGroupPct} onChange={(v) => setRisk({ maxExposurePerGroupPct: v })} step={0.01} min={0} max={1} hint={fmtPct(risk.maxExposurePerGroupPct, 0)} />
            <NumberField label="Max correlated" value={risk.maxCorrelatedPct} onChange={(v) => setRisk({ maxCorrelatedPct: v })} step={0.01} min={0} max={1} hint={fmtPct(risk.maxCorrelatedPct, 0)} />
            <NumberField label="Min edge to act" value={risk.minEdge} onChange={(v) => setRisk({ minEdge: v })} step={0.005} min={0} max={0.2} hint={fmtPct(risk.minEdge, 1)} />
          </div>
          <div className="grid cols-2">
            <SelectField label="Default Kelly mode" value={risk.kellyMode} onChange={(v: KellyMode) => setRisk({ kellyMode: v })} options={[{ value: 'full', label: 'Full Kelly' }, { value: 'half', label: 'Half Kelly' }, { value: 'quarter', label: 'Quarter Kelly' }, { value: 'flat', label: 'Flat staking' }]} />
            <NumberField label="Cooldown after N losses" value={risk.cooldownLossesTrigger} onChange={(v) => setRisk({ cooldownLossesTrigger: Math.round(v) })} min={1} max={10} />
          </div>
        </Card>

        <Card title="Live exposure" subtitle="Open positions grouped by correlation cluster">
          {breakdown.length === 0 ? (
            <div className="empty">No open positions.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Cluster</th><th>Positions</th><th>Exposure</th><th>% bankroll</th></tr></thead>
                <tbody>
                  {breakdown.map((g) => (
                    <tr key={g.group}>
                      <td>{g.group}</td>
                      <td className="mono">{g.items.length}</td>
                      <td className="mono">{fmtCurrency(g.amount)}</td>
                      <td className="mono"><span className={g.pctOfBankroll > risk.maxCorrelatedPct ? 'neg' : ''}>{fmtPct(g.pctOfBankroll, 1)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="stack" style={{ marginTop: 10 }}>
            {warnings.length === 0 ? (
              <Pill tone="green">{'\u2713'} Within all exposure limits</Pill>
            ) : (
              warnings.map((w, i) => <Banner key={i} tone={w.level === 'danger' ? 'danger' : 'warn'}>{w.message}</Banner>)
            )}
          </div>
        </Card>
      </div>

      <Card title="Risk-of-ruin & bankroll simulation" subtitle="Monte Carlo across many possible futures with these stakes">
        <div className="grid cols-4">
          <NumberField label="Win probability" value={winProb} onChange={setWinProb} step={0.01} min={0} max={1} />
          <NumberField label="Payout multiple" value={payout} onChange={setPayout} step={0.1} min={1} />
          <NumberField label="Stake fraction" value={stakeFraction} onChange={setStakeFraction} step={0.01} min={0.01} max={1} />
          <NumberField label="Number of bets" value={bets} onChange={(v) => setBets(Math.round(v))} step={10} min={10} />
        </div>
        <div className="grid cols-4">
          <Stat label="Risk of ruin" value={fmtPctValue(ror.riskOfRuinPct, 1)} tone={ror.riskOfRuinPct > 10 ? 'neg' : 'pos'} sub="lose ≥ half bankroll" />
          <Stat label="Median ending" value={fmtCurrency(ror.medianEnding)} />
          <Stat label="5th percentile" value={fmtCurrency(ror.p5Ending)} tone="neg" />
          <Stat label="Avg max drawdown" value={fmtPctValue(ror.avgMaxDrawdownPct, 0)} />
        </div>
        <LineChart series={mc.paths.slice(0, 24).map((p) => ({ values: p, color: 'var(--accent)' }))} height={220} yFormat={(n) => `$${n.toFixed(0)}`} />
        <div className="row between" style={{ marginTop: 8 }}>
          <span className="muted">Probability of ending below start: <span className="mono">{fmtPctValue(mc.probLossPct, 0)}</span></span>
          <Button size="sm" onClick={() => setStakeFraction(Math.max(0.01, stakeFraction / 2))}>Halve the stake</Button>
        </div>
      </Card>
    </div>
  )
}
